import { useEffect, useState } from 'react';
import { errorMessage, validationIssues } from '../api/client';
import { useStore } from '../state/store';
import { href, navigate } from '../router';
import { Icon, type IconName } from '../components/ui';
import '../styles/pages/discover.css';

export const PASSWORD_MIN_LENGTH = 10;

export function FieldError({ id, message }: { id: string; message?: string }) {
  if (!message) return null;
  return (
    <div id={id} className="field-error" role="alert">
      <Icon name="warning" size={14} /> {message}
    </div>
  );
}

/** Field errors from a validation_failed response keyed by field name. */
export function fieldErrors(e: unknown): Record<string, string> {
  const out: Record<string, string> = {};
  for (const issue of validationIssues(e)) out[issue.path.replace(/^body\./, '')] = issue.message;
  return out;
}

/** The navy half of the page: what an account is for. Same story either side of the sign-up line. */
export function StorySide({ heading, points, note }: { heading: string; points: { icon: IconName; title: string; body: string }[]; note: string }) {
  return (
    <aside className="story-side">
      <span className="eyebrow">TechCore Solutions</span>
      <h2 style={{ marginTop: 0 }}>{heading}</h2>
      <ul className="story-list">
        {points.map((p) => (
          <li key={p.title}>
            <span className="icon-tile on-navy" aria-hidden="true"><Icon name={p.icon} /></span>
            <span>
              <strong>{p.title}</strong>
              <span className="muted small">{p.body}</span>
            </span>
          </li>
        ))}
      </ul>
      <p className="story-note">{note}</p>
    </aside>
  );
}

export function LoginPage() {
  const { user, login } = useStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [fields, setFields] = useState<Record<string, string>>({});
  useEffect(() => {
    if (user) navigate({ page: 'dashboard' });
  }, [user]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);
    setFields({});
    if (!email.trim() || !password) {
      setFields({ ...(email.trim() ? {} : { email: 'Enter your email address.' }), ...(password ? {} : { password: 'Enter your password.' }) });
      return;
    }
    setBusy(true);
    try {
      await login(email.trim(), password);
      navigate({ page: 'dashboard' });
    } catch (err) {
      const f = fieldErrors(err);
      setFields(f);
      if (Object.keys(f).length === 0) setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth page-enter">
      <div className="form-side">
        <article className="form-inner">
          <span className="eyebrow">Welcome back</span>
          <h1>Log in</h1>
          <p className="muted">Your sessions, evaluations and workplace are where you left them.</p>
          <form onSubmit={(e) => void submit(e)} noValidate aria-busy={busy}>
            <div className="field">
              <label htmlFor="login-email">Email</label>
              <input id="login-email" type="text" inputMode="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={fields.email ? true : undefined} aria-describedby={fields.email ? 'login-email-err' : undefined} />
              <FieldError id="login-email-err" message={fields.email} />
            </div>
            <div className="field">
              <label htmlFor="login-password">Password</label>
              <input id="login-password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} aria-invalid={fields.password ? true : undefined} aria-describedby={fields.password ? 'login-password-err' : undefined} />
              <FieldError id="login-password-err" message={fields.password} />
            </div>
            <div aria-live="polite">{error && <div className="notice bad" role="alert">{error}</div>}</div>
            <div className="actions">
              <button type="submit" disabled={busy}>{busy ? 'Logging in…' : 'Log in'}</button>
              <a className="button quiet" href={href({ page: 'register' })}>Create an account</a>
            </div>
            <p className="small" style={{ marginTop: 'var(--sp-3)' }}><a href={href({ page: 'forgot' })}>Forgot your password?</a></p>
          </form>
          <p className="small muted">Exploring and comparing career tracks needs no account. <a href={href({ page: 'explore' })}>Browse the catalog</a>.</p>
        </article>
      </div>
      <StorySide
        heading="Pick up the shift where you left it."
        points={[
          { icon: 'briefcase', title: 'Your seat at TechCore', body: 'Tickets, inbox, colleagues and the projects you were part of.' },
          { icon: 'chart', title: 'Evidence that carries over', body: 'Skills you demonstrated in one track count toward related ones.' },
          { icon: 'target', title: 'Interview preparation', body: 'Question banks and mock rounds built on the work you did.' },
        ]}
        note="Practice in WorkSim AI demonstrates practice, not qualification. It does not guarantee employment."
      />
    </div>
  );
}

export function RegisterPage() {
  const { user, register } = useStore();
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [fields, setFields] = useState<Record<string, string>>({});
  useEffect(() => {
    if (user) navigate({ page: 'onboarding' });
  }, [user]);

  const strength = password.length === 0 ? 0 : password.length < PASSWORD_MIN_LENGTH ? 1 : password.length < 16 ? 2 : 3;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(undefined);
    const local: Record<string, string> = {};
    if (!displayName.trim()) local.displayName = 'Enter the name you want to be called.';
    if (!email.trim()) local.email = 'Enter your email address.';
    if (password.length < PASSWORD_MIN_LENGTH) local.password = `Use at least ${PASSWORD_MIN_LENGTH} characters.`;
    if (confirm !== password) local.confirm = 'The two passwords do not match.';
    setFields(local);
    if (Object.keys(local).length) return;
    setBusy(true);
    try {
      await register(email.trim(), password, displayName.trim());
      navigate({ page: 'onboarding' });
    } catch (err) {
      const f = fieldErrors(err);
      setFields(f);
      if (Object.keys(f).length === 0) setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="auth page-enter">
      <div className="form-side">
        <article className="form-inner">
          <span className="eyebrow">Free to start</span>
          <h1>Create an account</h1>
          <p className="muted">Next you will choose a track and meet your first scenario. Everything you pick can be changed later in your profile.</p>
          <form onSubmit={(e) => void submit(e)} noValidate aria-busy={busy}>
            <div className="field">
              <label htmlFor="reg-name">Display name</label>
              <input id="reg-name" type="text" autoComplete="name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} aria-invalid={fields.displayName ? true : undefined} aria-describedby={fields.displayName ? 'reg-name-err' : undefined} />
              <FieldError id="reg-name-err" message={fields.displayName} />
            </div>
            <div className="field">
              <label htmlFor="reg-email">Email</label>
              <input id="reg-email" type="text" inputMode="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={fields.email ? true : undefined} aria-describedby={fields.email ? 'reg-email-err' : undefined} />
              <FieldError id="reg-email-err" message={fields.email} />
            </div>
            <div className="field">
              <label htmlFor="reg-password">Password</label>
              <input id="reg-password" type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} aria-invalid={fields.password ? true : undefined} aria-describedby="reg-password-hint reg-password-err" />
              <div className="strength" aria-hidden="true">
                <i className={strength >= 1 ? 'on' : ''} />
                <i className={strength >= 2 ? 'on' : ''} />
                <i className={strength >= 3 ? 'on full' : ''} />
              </div>
              <div id="reg-password-hint" className="hint">At least {PASSWORD_MIN_LENGTH} characters. A short sentence is easier to remember than symbols. It must not repeat one character or contain your email name.</div>
              <FieldError id="reg-password-err" message={fields.password} />
            </div>
            <div className="field">
              <label htmlFor="reg-confirm">Confirm password</label>
              <input id="reg-confirm" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} aria-invalid={fields.confirm ? true : undefined} aria-describedby={fields.confirm ? 'reg-confirm-err' : undefined} />
              <FieldError id="reg-confirm-err" message={fields.confirm} />
            </div>
            <div aria-live="polite">{error && <div className="notice bad" role="alert">{error}</div>}</div>
            <div className="actions">
              <button type="submit" disabled={busy}>{busy ? 'Creating…' : 'Create account'}</button>
              <a className="button quiet" href={href({ page: 'login' })}>I already have an account</a>
            </div>
          </form>
          <p className="small muted">You can export or delete everything in your account at any time from your profile.</p>
        </article>
      </div>
      <StorySide
        heading="A virtual company that behaves like a real one."
        points={[
          { icon: 'ticket', title: 'Work, not quizzes', body: 'Tickets, requirements, incidents and stakeholders with their own priorities.' },
          { icon: 'sparkles', title: 'A mentor while you work', body: 'Ask why a decision is the right one, at the moment you have to make it.' },
          { icon: 'folder', title: 'A portfolio you keep', body: 'Every deliverable you write stays in your account as evidence of practice.' },
        ]}
        note="Career information here is general guidance. Completing simulations demonstrates practice, not qualification, and does not guarantee employment."
      />
    </div>
  );
}
